import { Input, Segmented, Table, Tag, Typography } from "antd"
import { useState } from "react"
import { hasValue } from "../../../helpers/nullable"
import { getPlanDisplayName } from "../../../domain/plans/plan"
import VSpace from "../../common/VSpace"
import { UserCountReportDataRow } from "./api"
import { useUserCountReportData } from "./queries"

const { Text } = Typography

const filterRows = (rows: UserCountReportDataRow[] | undefined, filterOption: string, searchText: string) => {
  let result = rows ?? []
  result = result.filter(row => row.tenantName.toLowerCase().includes(searchText.toLowerCase()))

  if (filterOption === 'overages') {
    result = result.filter(row => row.actualUserCount > (row.assignedUserCount ?? 0))
  }

  if (filterOption === 'unassigned') {
    result = result.filter(row => !hasValue(row.assignedUserCount))
  }

  if(filterOption === 'underages') {
    result = result.filter(row => row.actualUserCount < (row.assignedUserCount ?? 0))
  }

  if(filterOption === 'attention') {
    result = result.filter(
      ((row => needsAttention(row)))
    )
  }

  return result
}

const needsAttention = (row: UserCountReportDataRow) => {
  return (row.planName === 'PRO_PLAN' && row.assignedUserCount > 5 && row.assignedUserCount !== row.actualUserCount) ||
    (row.planName === 'STANDARD_PLAN' && row.assignedUserCount > 2 && row.assignedUserCount !== row.actualUserCount) ||
    (row.planName === 'FOUNDING_MEMBER_PLAN' && row.assignedUserCount > 2 && row.assignedUserCount !== row.actualUserCount) ||
    (row.actualUserCount > (row.assignedUserCount ?? 0))
}

const UserCountReport = () => {
  const userCountReportDataQuery = useUserCountReportData()
  const [searchText, setSearchText] = useState('')
  const [filterOption, setFilterOption] = useState('overages')

  return <VSpace>
    <Input.Search placeholder="Filter tenant name" value={searchText} onChange={e => setSearchText(e.target.value)} />
    <Segmented
      options={[
        { label: 'Overages', value: 'overages' },
        { label: 'Unassigned', value: 'unassigned' },
        { label: 'Underages', value: 'underages' },
        { label: 'Needs Attention', value: 'attention' },
        { label: 'All', value: 'all' },
      ]}
      value={filterOption}
      onChange={value => setFilterOption(value)}
    />
    <Table
      dataSource={filterRows(userCountReportDataQuery.data, filterOption, searchText)}
      rowKey="tenantId"
      columns={[
        {
          title: 'Tenant',
          dataIndex: 'tenantName',
          sorter: (a: UserCountReportDataRow, b: UserCountReportDataRow) => a.tenantName.localeCompare(b.tenantName),
          render: (_, record: UserCountReportDataRow) => {
            return <a target="_blank" href={`/#!/tenants?id=${record.tenantId}`}>{record.tenantName}</a>
          }
        },
        {
          title: 'Plan',
          dataIndex: 'planName',
          render: (planName: string) => getPlanDisplayName(planName)
        },
        {
          title: 'Assigned Users',
          dataIndex: 'assignedUserCount',
          sorter: (a: UserCountReportDataRow, b: UserCountReportDataRow) => (a.assignedUserCount ?? 0) - (b.assignedUserCount ?? 0),
          render: (assignedUserCount: number | undefined) => {
            if (hasValue(assignedUserCount)) {
              return assignedUserCount
            }

            return <Tag color='red'>Not Assigned</Tag>
          }
        },
        {
          title: 'Actual Users',
          dataIndex: 'actualUserCount',
          sorter: (a: UserCountReportDataRow, b: UserCountReportDataRow) => a.actualUserCount - b.actualUserCount,
          render: (actualUserCount: number, record: UserCountReportDataRow) => {
            if (actualUserCount > (record.assignedUserCount ?? 0)) {
              return <Text type="danger">{actualUserCount}</Text>
            }

            return actualUserCount
          }
        }
      ]}
    />
  </VSpace>
}

export default UserCountReport
